import React, { useState, useEffect, useCallback } from "react";
import { Star } from "lucide-react";
import { useAuth } from "../AuthContext";
import { API_URL } from "../config";

function Stars({ value, onHover, onClick, size = 18, interactive = false }) {
  return (
    <div style={{ display: "inline-flex", gap: 2 }}>
      {[1, 2, 3, 4, 5].map(n => (
        <Star
          key={n}
          size={size}
          strokeWidth={1.8}
          color={n <= value ? "#f59e0b" : "#d1d5db"}
          fill={n <= value ? "#f59e0b" : "none"}
          style={interactive ? { cursor: "pointer" } : undefined}
          onMouseEnter={interactive ? () => onHover(n) : undefined}
          onClick={interactive ? () => onClick(n) : undefined}
        />
      ))}
    </div>
  );
}

/**
 * compact — только звёзды + средняя оценка, без списка отзывов (для карточек
 * списков, например Events.js). Полная версия (по умолчанию) добавляет форму
 * с комментарием и список отзывов коллег — для страницы материала.
 */
export default function RatingWidget({ contentType, contentId, onLoginRequest, compact = false }) {
  const { isLoggedIn, token } = useAuth();
  const [summary, setSummary] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadSummary = useCallback(() => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${API_URL}/ratings/summary?content_type=${contentType}&content_id=${contentId}`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(setSummary)
      .catch(() => {});
  }, [contentType, contentId, token]);

  const loadReviews = useCallback(() => {
    if (compact) return;
    fetch(`${API_URL}/ratings?content_type=${contentType}&content_id=${contentId}`)
      .then(r => r.ok ? r.json() : [])
      .then(setReviews)
      .catch(() => {});
  }, [contentType, contentId, compact]);

  useEffect(() => { loadSummary(); loadReviews(); }, [loadSummary, loadReviews]);

  const submit = async score => {
    if (!isLoggedIn) { onLoginRequest && onLoginRequest(); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/ratings`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content_type: contentType, content_id: contentId, score, comment: comment.trim() || null }),
      });
      if (r.ok) {
        setSummary(await r.json());
        setShowForm(false);
        setComment("");
        loadReviews();
      }
    } finally {
      setBusy(false);
    }
  };

  if (!summary) return null;

  if (compact) {
    const displayValue = hover || summary.my_score || summary.avg;
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 6 }} onMouseLeave={() => setHover(0)}>
        <Stars value={displayValue} size={15} onHover={setHover} onClick={submit} interactive />
        <span style={{ fontSize: "0.8rem", color: "#888" }}>
          {summary.count > 0 ? `${summary.avg.toFixed(1)} (${summary.count})` : "Оцените первым"}
        </span>
        {busy && <span style={{ fontSize: "0.78rem", color: "#aaa" }}>…</span>}
      </div>
    );
  }

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <Stars value={summary.avg} size={18} />
        <span style={{ fontSize: "0.88rem", color: "#888" }}>
          {summary.count > 0 ? `${summary.avg.toFixed(1)} из 5 · ${summary.count} оценок` : "Пока нет оценок"}
        </span>
        <button onClick={() => setShowForm(s => !s)}
          style={{ background: "none", border: "none", color: "#e5005b", fontWeight: 600, fontSize: "0.85rem", cursor: "pointer", padding: 0 }}>
          {summary.my_score ? "Изменить мою оценку" : "Оценить"}
        </button>
      </div>

      {showForm && (
        <div style={{ marginTop: 10, background: "#f7f8fc", borderRadius: 8, padding: "10px 14px" }}>
          <Stars value={hover || summary.my_score || 0} onHover={setHover} onClick={submit} size={22} interactive />
          <textarea value={comment} onChange={e => setComment(e.target.value)} rows={2} maxLength={1000}
            placeholder="Отзыв (необязательно) — сохранится вместе с оценкой при клике на звёзды"
            style={{ display: "block", width: "100%", marginTop: 8, padding: "7px 10px", borderRadius: 6, border: "1px solid #c4ceda", fontSize: "0.88rem", boxSizing: "border-box" }}
            onMouseLeave={() => setHover(0)} />
          {busy && <span style={{ fontSize: "0.8rem", color: "#888" }}>Сохраняем…</span>}
        </div>
      )}

      {reviews && reviews.filter(r => r.comment).length > 0 && (
        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
          {reviews.filter(r => r.comment).map(r => (
            <div key={r.id} style={{ borderLeft: "3px solid #e5e7eb", paddingLeft: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Stars value={r.score} size={13} />
                <span style={{ fontWeight: 600, fontSize: "0.86rem", color: "#374151" }}>{r.author_name}</span>
              </div>
              <p style={{ margin: "3px 0 0", fontSize: "0.88rem", color: "#4b5563" }}>{r.comment}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
